/* eslint-disable react/require-default-props */
import React from 'react';
import { translate } from '@utils/i18n';
import { Tag } from '@ui/atoms/Tag';
import DeviceInfoToggle from '@features/IIMReview/components/IssueCard/DeviceInfoToggle';
import { IssueStatusEnum } from '../../types';
import ManagerNote from './ManagerNote';
import IssueLayout from './IssueLayout';
import './styles.scss';
// Component to render attributed notes with semantic diff markup
function AttributedNotesRenderer({ attributedNotes }) {
    const renderAttributedText = (text) => {
        // Parse the markup and convert to React elements
        const parts = [];
        let currentIndex = 0;
        let key = 0;
        // Regular expression to match our three tag types
        const tagRegex = /<(swap|add|del)(?:\s+original="([^"]*)")?>([^<]*)<\/\1>/g;
        let match = tagRegex.exec(text);
        while (match !== null) {
            const [fullMatch, tagType, originalText, content] = match;
            const matchStart = match.index;
            // Add any text before this match
            if (matchStart > currentIndex) {
                parts.push(text.slice(currentIndex, matchStart));
            }
            // Handle different tag types
            if (tagType === 'swap') {
                parts.push(React.createElement("span", { key: key, className: "attributed-notes__swap-container" },
                    React.createElement("span", { className: "attributed-notes__swap-original" }, originalText || ''),
                    React.createElement("span", { className: "attributed-notes__swap-corrected" }, content)));
                key += 1;
            }
            else if (tagType === 'add') {
                parts.push(React.createElement("span", { key: key, className: "attributed-notes__add" }, content));
                key += 1;
            }
            else if (tagType === 'del') {
                parts.push(React.createElement("span", { key: key, className: "attributed-notes__del", title: "This text was removed" }, content));
                key += 1;
            }
            currentIndex = matchStart + fullMatch.length;
            match = tagRegex.exec(text);
        }
        // Add any remaining text
        if (currentIndex < text.length) {
            parts.push(text.slice(currentIndex));
        }
        return parts;
    };
    if (!attributedNotes) {
        return null;
    }
    return React.createElement("div", { className: "attributed-notes" }, renderAttributedText(attributedNotes));
}
const getTechnicianCorrection = (issue) => {
    var _a, _b, _c, _d, _e, _f;
    const originalNote = ((_a = issue.issueData.clarityCheckResult) === null || _a === void 0 ? void 0 : _a.notes) || ((_b = issue.issueData.grammarCheckResult) === null || _b === void 0 ? void 0 : _b.notes);
    return ((_d = (_c = issue.deficiency) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.notes) === originalNote
        ? translate('no_correction_made', { namespace: 'features.iim_review.issue_card' })
        : ((_f = (_e = issue.deficiency) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.notes) || '';
};
export default function AnalyzeDeficiencyNoteIssueCard({ issue, defaultExpanded, onToggle, onDeviceInfoToggle, onUpdateIssue, onUndoAction, readOnly = false, settings }) {
    var _a, _b, _c, _d;
    const status = issue.status || IssueStatusEnum.Detected;
    const isHighImpact = issue.status !== IssueStatusEnum.Resolved && ((_a = issue.issueData) === null || _a === void 0 ? void 0 : _a.highImpact) === true;
    // Handler functions for issue actions
    const handleSuggestCorrection = () => {
        onUpdateIssue(issue.id, status, true);
    };
    const handleDismissIssue = () => {
        onUpdateIssue(issue.id, IssueStatusEnum.DismissedByBackOffice, false);
    };
    const handleNoteChange = (note) => {
        onUpdateIssue(issue.id, status, issue.markedForSuggestion, note);
    };
    // Determine the issue type based on which check result is present
    const getIssueType = () => {
        const captionSuffix = isPhotoCaption
            ? ` (${translate('photo_caption_source', { namespace: 'features.iim_review.issue_card' })})`
            : '';
        if (issue.issueData.grammarCheckResult && issue.issueData.clarityCheckResult) {
            return `${translate('grammar_spelling_and_style', {
                namespace: 'features.iim_review.issue_card'
            })}${captionSuffix}`;
        }
        if (issue.issueData.grammarCheckResult) {
            return `${translate('grammar_corrections', { namespace: 'features.iim_review.issue_card' })}${captionSuffix}`;
        }
        if (issue.issueData.clarityCheckResult) {
            return `${translate('writing_clarity', { namespace: 'features.iim_review.issue_card' })}${captionSuffix}`;
        }
        // Default fallback
        return `${translate('grammar_spelling_and_style', {
            namespace: 'features.iim_review.issue_card'
        })}${captionSuffix}`;
    };
    const technicianCorrection = getTechnicianCorrection(issue);
    const isPhotoCaption = issue.issueData.noteSource === 'photo_caption';
    // Create high impact badge if needed
    const highImpactBadge = isHighImpact ? (React.createElement("span", { className: "issue-card__badge issue-card__badge--high-impact" }, translate('high_impact', { namespace: 'features.iim_review.issue_card' }))) : undefined;
    return (React.createElement(IssueLayout, { issueType: getIssueType(), badge: highImpactBadge, status: status, defaultExpanded: defaultExpanded, onToggle: onToggle, onSuggestCorrection: handleSuggestCorrection, onDismissIssue: handleDismissIssue, onUndoAction: onUndoAction, readOnly: readOnly, statusChanges: issue.statusChanges, isMultitech: issue.isMultitech, technician: issue.technician, markedForSuggestion: issue.markedForSuggestion },
        React.createElement(React.Fragment, null,
            issue.issueData.clarityCheckResult && (React.createElement("div", { className: "issue-card__body" },
                React.createElement("div", { className: "clarity-result" },
                    React.createElement("div", { className: "clarity-result__question" },
                        React.createElement("strong", null, issue.issueData.clarityCheckResult.questionText)),
                    React.createElement("div", { className: "clarity-result__summary" }, issue.issueData.clarityCheckResult.categorizationSummary),
                    React.createElement("div", { className: "clarity-result__notes-section" },
                        React.createElement("div", { className: "clarity-result__notes-title" },
                            React.createElement("strong", null, "Notes")),
                        React.createElement("div", { className: "clarity-result__original-note" }, issue.issueData.clarityCheckResult.notes),
                        React.createElement("div", { className: "clarity-result__issues" },
                            React.createElement("div", { className: "clarity-result__issues-title" },
                                React.createElement("strong", null, "Issues Identified:")),
                            React.createElement("div", { className: "clarity-result__categories" },
                                issue.issueData.clarityCheckResult.containsProfanity && (React.createElement("div", { className: "clarity-result__category" },
                                    React.createElement(Tag, { label: translate('contains_profanity', { namespace: 'features.iim_review.issue_card' }), variant: "red", colorStyle: "solid", readOnly: true }))),
                                issue.issueData.clarityCheckResult.categories.map((category, index) => (React.createElement("div", { key: index, className: "clarity-result__category" },
                                    React.createElement("span", { className: "clarity-result__category-badge" }, category === 'UNCLEAR' ? 'Unclear' : 'Passive Voice'),
                                    React.createElement("span", { className: "clarity-result__category-explanation" }, category === 'UNCLEAR'
                                        ? translate('unclear_explanation', { namespace: 'features.iim_review.issue_card' })
                                        : translate('passive_voice_explanation', { namespace: 'features.iim_review.issue_card' }))))))))))),
            issue.issueData.grammarCheckResult && (React.createElement("div", { className: "issue-card__body" },
                React.createElement("div", { className: "grammar-result" },
                    React.createElement("div", { className: "grammar-result__question" },
                        React.createElement("strong", null, issue.issueData.grammarCheckResult.questionText)),
                    issue.issueData.grammarCheckResult.containsProfanity && (React.createElement("div", { className: "grammar-result__profanity" },
                        React.createElement(Tag, { label: translate('contains_profanity', { namespace: 'features.iim_review.issue_card' }), variant: "red", colorStyle: "solid", readOnly: true }))),
                    React.createElement("div", { className: "grammar-result__answer" }, issue.issueData.grammarCheckResult.correctionSummary),
                    React.createElement("div", { className: "grammar-result__notes-section" },
                        React.createElement("div", { className: "grammar-result__notes-title" },
                            React.createElement("strong", null, "Notes")),
                        React.createElement("div", { className: "grammar-result__notes-content" },
                            React.createElement(AttributedNotesRenderer, { attributedNotes: issue.issueData.grammarCheckResult.attributedNotes })))))),
            status === IssueStatusEnum.Resolved && ((_c = (_b = issue.deficiency) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.notes) && (React.createElement("div", { className: "issue-card__body" },
                React.createElement("div", { className: "technician-correction" },
                    React.createElement("div", { className: "technician-correction__title" },
                        React.createElement("strong", null, "Technician Correction")),
                    React.createElement("div", { className: "technician-correction__content" }, technicianCorrection)))),
            React.createElement(DeviceInfoToggle, { inspectable: issue.inspectable, onToggle: onDeviceInfoToggle }),
            issue.markedForSuggestion && (!readOnly || ((_d = issue.managerNote) === null || _d === void 0 ? void 0 : _d.trim())) && (React.createElement(ManagerNote, { note: issue.managerNote || '', onNoteChange: handleNoteChange, disabled: readOnly })))));
}
